<template>
    <div class="px-4 pt-6 pb-14 border-b border-secondary lg:border lg:rounded lg:p-6" data-cy="customer-order">
        <div class="pb-6 mb-6 border-b border-secondary lg:flex lg:flex-wrap lg:items-center">
            <div
                class="badge badge-md rounded mb-3 lg:order-3 lg:mb-0 lg:ml-4"
                :class="{
                    'badge-secondary': order.stateMachineState?.technicalName === 'open',
                    'badge-neutral': order.stateMachineState?.technicalName === 'in_progress',
                    'badge-new': order.stateMachineState?.technicalName === 'completed',
                }"
                v-text="order.stateMachineState?.translated?.name"
            />

            <div class="text-base-content-subtle font-semibold mb-1 lg:order-1 lg:mb-0 lg:mr-1">
                #{{ order.orderNumber }} <span class="hidden lg:inline">—</span>
            </div>

            <div class="font-semibold mb-4 lg:order-2 lg:mb-0">
                {{ $t('customer.order.from') }} {{ formatDate(order.orderDate) }}
            </div>

            <div class="text-sm lg:order-4 lg:w-full lg:mt-4">
                {{ $t('customer.order.shippingMethod') }}: {{ delivery?.shippingMethod?.translated?.name }} <br class="lg:hidden"> <span class="lg:mr-6" />
                {{ $t('customer.order.paymentMethod') }}:
                <span v-if="stripePayment != null">{{ $t(`customer.order.stripe.${stripePayment}`) }}</span>
                <span v-else-if="shopwarePaymentName != null">{{ shopwarePaymentName }}</span>

                <span
                    class="badge badge-md rounded"
                    :class="{
                        'badge-new': transaction?.stateMachineState?.technicalName === 'paid'
                    }"
                >{{ transaction?.stateMachineState?.translated?.name }}</span>
            </div>
        </div>

        <div class="lg:flex lg:justify-between lg:items-center lg:gap-6">
            <div class="flex items-center lg:shrink-0">
                <div
                    class="radial-progress text-primary bg-secondary mr-2 shrink-0"
                    :style="`--size: 1.5rem; --thickness: 4px; ${delivery?.stateMachineState?.technicalName === 'open' ? '--value:25;' : ''} ${delivery?.stateMachineState?.technicalName === 'shipped' ? '--value:100;' : ''} `"
                    role="progressbar"
                >
                    <div class="absolute top-0 right-0 bottom-0 left-0 m-auto w-4 h-4 bg-base-100 rounded-full" />
                    <MiscIcon
                        v-if="delivery?.stateMachineState?.technicalName === 'shipped'"
                        name="check"
                        size="sm"
                        class="z-10"
                    />
                </div>
                <div class="font-semibold mr-3">
                    <template v-if="delivery?.stateMachineState?.technicalName === 'open'">
                        {{ $t('customer.order.orderShippingOpen') }}
                    </template>
                    <template v-if="delivery?.stateMachineState?.technicalName === 'shipped'">
                        {{ $t('customer.order.orderShipped') }}
                    </template>
                </div>
                <div v-if="delivery?.updatedAt != null" class="text-base-content-subtle">
                    <template v-if="calculateDays(delivery.updatedAt) === 1">
                        {{ $t('customer.order.today') }}
                    </template>
                    <template v-else>
                        {{ $t('customer.order.daysAgo', { days: calculateDays(delivery.updatedAt) }) }}
                    </template>
                </div>
            </div>

            <div v-if="delivery?.trackingCodes?.[0]" class="w-full mt-3 lg:mt-0 lg:w-auto lg:mr-auto">
                <a
                    v-if="delivery.shippingMethod?.trackingUrl != null"
                    class="link link-primary font-semibold text-sm"
                    :href="delivery.shippingMethod.trackingUrl.replace('%s', delivery.trackingCodes![0])"
                    target="_blank"
                    rel="noopener"
                >{{ $t('customer.order.tracking') }}</a>
                <div v-else class="text-primary font-semibold text-sm">
                    {{ $t('customer.order.tracking') }}: {{ delivery.trackingCodes[0] }}
                </div>
            </div>

            <button type="button" class="btn btn-sm btn-secondary mt-6 lg:mt-0" @click="showDetails = !showDetails">
                <template v-if="showDetails">
                    {{ $t('customer.order.hideDetails') }}
                </template>
                <template v-else>
                    {{ $t('customer.order.showDetails') }}
                </template>
                <MiscIcon :name="!showDetails ? 'chevron-down' : 'chevron-up'" />
            </button>
        </div>

        <div v-if="showDetails" class="lg:flex lg:items-start lg:gap-6">
            <div class="lg:w-1/2">
                <MiscLink
                    to="/widerrufsbelehrung-widerrufsformular"
                    class="btn btn-sm btn-secondary mt-6 lg:mt-6"
                >
                    {{ $t('customer.order.revocationLink') }}
                    <MiscIcon name="arrow-right" size="sm" />
                </MiscLink>
                <div class="w-full mt-6 mb-6 pb-6 border-b border-secondary text-sm text-gray-500 font-light lg:border lg:rounded lg:p-4">
                    <div class="flex flex-col gap-2">
                        <div class="flex justify-between items-center text-base text-secondary-content">
                            <div class="flex gap-3 items-baseline">
                                <div class="text-xl text-secondary-content font-semibold">
                                    {{ $t('customer.order.totalGross') }}
                                </div>
                                <div class="text-xs text-base-content-subtle font-normal">
                                    <client-only>
                                        <span v-if="isDisplayGross">
                                            {{ $t('customer.order.includingTax') }}
                                        </span>
                                        <span v-else>
                                            {{ $t('customer.order.excludingTax') }}
                                        </span>
                                    </client-only>
                                </div>
                            </div>
                            <div class="text-xl font-semibold text-secondary-content">
                                {{ getFormattedPrice(order.price?.totalPrice, { forceFraction: true }) }}
                            </div>
                        </div>
                        <div v-if="order.shippingTotal != null" class="flex justify-between items-center text-secondary-content">
                            <div>{{ $t('customer.order.shipping') }}</div>
                            <div>{{ getFormattedPrice(order.shippingTotal, { forceFraction: true }) }}</div>
                        </div>
                        <div class="flex justify-between items-center text-secondary-content">
                            <div>{{ $t('customer.order.totalNet') }}</div>
                            <div>{{ getFormattedPrice(order.amountNet, { forceFraction: true }) }}</div>
                        </div>
                        <div v-if="firstTax" class="flex justify-between items-center text-secondary-content">
                            <div>{{ $t('customer.order.tax') }} ({{ firstTax.taxRate }}%)</div>
                            <div>{{ getFormattedPrice(firstTax.tax, { forceFraction: true }) }}</div>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col gap-4 pb-6 mb-6 border-b border-secondary lg:border lg:p-6 lg:flex-row lg:gap-6">
                    <div v-if="delivery?.shippingOrderAddress" class="flex flex-col gap-2 lg:w-1/2">
                        <div class="text-lg">
                            {{ $t('customer.order.shippingAddress') }}
                        </div>
                        <div class="text-sm">
                            <CustomerAddressRenderer :address="delivery.shippingOrderAddress" />
                        </div>
                    </div>
                    <div v-if="order.billingAddress" class="col-span-12 md:col-span-6 flex flex-col gap-2 lg:w-1/2">
                        <div class="text-lg">
                            {{ $t('customer.order.billingAddress') }}
                        </div>
                        <div class="text-sm">
                            <CustomerAddressRenderer :address="order.billingAddress" />
                        </div>
                    </div>
                </div>

                <div
                    v-if="order.documents?.length > 0"
                    class="w-full mt-6 mb-6 pb-6 border-b border-secondary lg:border lg:rounded lg:p-4"
                >
                    <div class="text-xl">
                        {{ $t('customer.order.tableDocument') }}
                    </div>
                    <div v-for="document in order.documents" :key="document.id" class="flex justify-between items-center p-2 gap-2">
                        <div
                            role="button"
                            tabindex="0"
                            class="link flex lg:col-span-5 items-center text-sm"
                            @click="openDocument(document)"
                            @keydown.enter="openDocument(document)"
                        >
                            <MiscIcon name="file" class="w-5 h-5 mr-2" />
                            <div class="break-words" v-text="buildDocumentName(document)" />
                        </div>
                        <button type="button" class="btn btn-outline shrink-0" @click="downloadDocument(document)">
                            {{ $t('customer.order.viewFile') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="lg:w-1/2 lg:border lg:border-secondary lg:px-4 lg:py-2 lg:mt-6">
                <div
                    v-for="(item, index) in order.lineItems"
                    :key="item.id"
                    class="flex items-start py-4"
                    :class="index + 1 >= order.lineItems!.length ? 'lg:pb-0' : ''"
                >
                    <div class="avatar indicator mr-4">
                        <span v-if="item?.type !== 'promotion'" class="indicator-item indicator-top badge border rounded-full border-secondary bg-primary text-xs text-base-100 text-center p-2">{{ item.quantity }} x</span>
                        <div class="w-12 h-12">
                            <NuxtImg
                                v-if="lineItemImageUrl(item)"
                                :src="lineItemImageUrl(item)"
                                width="48"
                                height="48"
                                :alt="item.label"
                            />
                            <div v-else-if="item?.type === 'promotion'" />
                            <img
                                v-else
                                src="~/assets/product/placeholder-image.png"
                                class="d-block h-auto"
                                :alt="item.label"
                            >
                        </div>
                    </div>

                    <div class="w-full flex justify-between pb-6" :class="index + 1 >= order.lineItems!.length ? 'border-none' : 'border-b'">
                        <div class="w-full flex flex-col justify-between gap-2 mr-4 font-semibold text-sm">
                            <div>{{ item.label }}</div>
                            <i18n-t
                                v-if="item.payload?.customFields?.titleist_logoball"
                                keypath="customer.order.logoBallInfo"
                                tag="div"
                                class="font-normal"
                            >
                                <a
                                    :href="setTallyLink()"
                                    class="link text-sale font-semibold"
                                    target="_blank"
                                    rel="noopener"
                                >{{ $t('customer.order.logoBallLink') }}</a>
                            </i18n-t>
                            <i18n-t
                                v-else-if="item.payload?.customFields?.team_clothing"
                                keypath="customer.order.logoBallInfo"
                                tag="div"
                                class="font-normal"
                            >
                                <a
                                    :href="setTallyTeamwearLink()"
                                    class="link text-sale font-semibold"
                                    target="_blank"
                                    rel="noopener"
                                >{{ $t('customer.order.logoBallLink') }}</a>
                            </i18n-t>
                        </div>
                        <div class="text-right text-sm font-semibold">
                            {{ getFormattedPrice(item.totalPrice, { forceFraction: true }) }}
                        </div>
                        <div class="flex flex-col">
                            <div
                                v-for="download in filterDownloads(item.downloads)"
                                :key="download.id"
                                role="button"
                                tabindex="0"
                                class="link"
                                @click="downloadLineItemFile(download)"
                                @keydown.enter="downloadLineItemFile(download)"
                            >
                                {{ download.media.fileName }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button type="button" class="btn btn-sm btn-secondary mt-3 lg:hidden" @click="showDetails = !showDetails">
                <template v-if="showDetails">
                    {{ $t('customer.order.hideDetails') }}
                </template>
                <template v-else>
                    {{ $t('customer.order.showDetails') }}
                </template>
                <MiscIcon :name="!showDetails ? 'chevron-down' : 'chevron-up'" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
    useRuntimeConfig,
    usePrice,
    useSessionContext,
    useNotification,
    useDate,
    useUser,
    useOrderDetails,
} from '#imports'
import type { Schemas } from '#shopware'

const props = defineProps<{
    order: Schemas['Order']
}>()

const config = useRuntimeConfig().public
const { getFormattedPrice } = usePrice()
const { sessionContext } = useSessionContext()

const isDisplayGross = computed(() => sessionContext.value?.currentCustomerGroup?.displayGross !== false)
const { showNotification } = useNotification()
const { formatDate } = useDate()
const { user } = useUser()
const { getDocumentFile, getMediaFile } = useOrderDetails(props.order.id)
const showDetails = ref(false)

const delivery = computed(() => props.order.deliveries?.[0])
const transaction = computed(() => props.order.transactions?.[0])

const stripePayment = computed(() => {
    return props.order.customFields?.stripe_payment_context?.payment?.payment_method_type
})

const shopwarePaymentName = computed(() => {
    return props.order.transactions?.[0]?.paymentMethod?.translated?.name ?? null
})

const firstTax = computed(() => props.order.price?.calculatedTaxes?.[0] ?? null)

function filterDownloads (downloads: Schemas['OrderLineItemDownload'][] | undefined) {
    return downloads?.filter(d => d.accessGranted) ?? []
}

function lineItemImageUrl (item: Schemas['OrderLineItem']) {
    return item.payload?.courseframe?.imageUrl ?? item.cover?.url
}

function buildDocumentName (document: Schemas['Document']): string {
    const d = new Date(document.config.documentDate ?? document.createdAt ?? '')
    const date = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
    return `${document.config.name.replace(' ', '-')}_${document.config.documentNumber ?? ''}_${date}.${document.fileType ?? 'pdf'}`
}

async function downloadDocument (document: Schemas['Document']) {
    try {
        const blob = await getDocumentFile(document.id, document.deepLinkCode ?? '')
        if (blob instanceof Blob) {
            triggerDownload(blob, buildDocumentName(document))
        }
    } catch (e) {
        showNotification(e instanceof Error ? e.message : String(e), 'error', true)
    }
}

async function openDocument (document: Schemas['Document']) {
    try {
        const blob = await getDocumentFile(document.id, document.deepLinkCode ?? '')
        if (blob instanceof Blob) {
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 7000)
        }
    } catch (e) {
        showNotification(e instanceof Error ? e.message : String(e), 'error', true)
    }
}

async function downloadLineItemFile (download: Schemas['OrderLineItemDownload']) {
    try {
        const blob = await getMediaFile(download.id)
        triggerDownload(blob, download.media.fileName ?? download.id)
    } catch (e) {
        showNotification(e instanceof Error ? e.message : String(e), 'error', true)
    }
}

function triggerDownload (blob: Blob, fileName: string) {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(link.href), 7000)
}

function calculateDays (date: string) {
    return Math.ceil((new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24))
}

function setTallyLink () {
    if (!config.tallyFormLogoBall || (config.tallyFormLogoBall as string).trim() === '') {
        console.error('Missing environment variable: TALLYFORM_LOGOBALL')
        return '#'
    }
    return (config.tallyFormLogoBall as string)
        .replace('{LASTNAME}', encodeURIComponent(user.value?.lastName ?? ''))
        .replace('{FIRSTNAME}', encodeURIComponent(user.value?.firstName ?? ''))
        .replace('{EMAIL}', encodeURIComponent(props.order.orderCustomer?.email ?? ''))
        .replace('{ORDERNO}', encodeURIComponent(props.order.orderNumber ?? ''))
        .replace('{CUSTOMERNO}', encodeURIComponent(user.value?.customerNumber ?? ''))
}

function setTallyTeamwearLink () {
    if (!config.tallyFormTeamwear || (config.tallyFormTeamwear as string).trim() === '') {
        console.error('Missing environment variable: TALLYFORM_TEAMWEAR')
        return '#'
    }
    return (config.tallyFormTeamwear as string)
        .replace('{LASTNAME}', encodeURIComponent(user.value?.lastName ?? ''))
        .replace('{FIRSTNAME}', encodeURIComponent(user.value?.firstName ?? ''))
        .replace('{EMAIL}', encodeURIComponent(props.order.orderCustomer?.email ?? ''))
        .replace('{ORDERNO}', encodeURIComponent(props.order.orderNumber ?? ''))
        .replace('{CUSTOMERNO}', encodeURIComponent(user.value?.customerNumber ?? ''))
}
</script>
